import axios from "axios";
import { API_URL } from '../Constants';

// Convert a base64 string into a binary Blob for efficient multipart uploads.
const base64ToBlob = (base64, mimeType = 'application/octet-stream') => {
    const byteCharacters = atob(base64);
    const sliceSize = 512;
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: mimeType });
};

class FieldServiceAPI{
    getServiceTechnicians(accessToken) {
        return axios.get(`${API_URL}getTechnicians/`,{headers:{'Authorization':accessToken}});
    };

    getAllAssociatedHelpers = async (sessionId) => {
        return axios.get(`${API_URL}getAllAssociatedHelpersFS/${sessionId}`);
    }

    getAppointments(accessToken, branch) {
        return axios.get(`${API_URL}getFSAppointments/${branch}`,{headers:{'Authorization':accessToken}});
    };

    getAppointmentsByUserEmail = async (token, userEmail) => {
        return axios.get(`${API_URL}appointmentsByTech/${userEmail}`, {headers:{'Authorization':token}});
    };

    getCustomersAndLocations = async (token) => {
        return axios.get(`${API_URL}getFSCustomersAndLocations/`,{headers:{'Authorization':token}});
    };

    getAllCustomers = async (token) => {
        return axios.get(`${API_URL}getFSCustomers/`,{headers:{'Authorization':token}});
    };

    getCustomerLocations = async (token, customerId) => {
        return axios.get(`${API_URL}getFSCustomerLocations/${customerId}`,{headers:{'Authorization':token}});
    };

    getPreviousAppointments = async (token, customerId, locationId) => {
        return axios.get(`${API_URL}getPreviousFSAppointments/${customerId}/${locationId}`,{headers:{'Authorization':token}});
    }

    createAppointment = async (token, appointmentData) => {
        return axios.post(`${API_URL}createAppointment/`, appointmentData, {headers:{'Authorization':token}});
    };

    getAppointmentById = async (token, appointmentId) => {
        return axios.get(`${API_URL}getAppointmentDetails/${appointmentId}`, {headers:{'Authorization':token}});
    };

    updateAppointment = async (token, appointmentData) => {
        return axios.post(`${API_URL}updateAppointment/`, appointmentData, {headers:{'Authorization':token}});
    };

    changeAppointmentDate = async (token, appointmentId, newStartDate) => {
        return axios.post(`${API_URL}changeAppointmentDate`, {appointmentID: appointmentId, newStartDate}, {headers:{'Authorization':token}});
    };

    updateAppointmentStatus = async (token, appointmentNbr, newStatus) => {
        return axios.post(`${API_URL}updateAppointmentStatus/${appointmentNbr}`, {status:newStatus}, {headers:{'Authorization':token}});
    };

    deleteAppointment = async (token, appointmentId) => {
        return axios.delete(`${API_URL}deleteAppointment/${appointmentId}`, {headers:{'Authorization':token}});
    };

    getAppointmentBySession = async (sessionId, appointmentNumber) => {
        return axios.get(`${API_URL}getAppointmentBySession/${appointmentNumber}/${sessionId}`);
    };

    completeAppointment = async (completionData) => {
        // Separate binary attachments from the rest of the (text) payload so we can
        // send files as multipart/form-data instead of base64-inflated JSON. This
        // avoids 413 (Content Too Large) errors when many photos are attached.
        const { attachments = [], ...payload } = completionData;

        const formData = new FormData();
        formData.append('payload', JSON.stringify(payload));

        attachments.forEach((attachment) => {
            if (!attachment) return;

            // Attachments captured/cached in the UI are stored as base64. Convert
            // them back to binary Blobs for efficient multipart upload.
            if (attachment.contentBase64) {
                const blob = base64ToBlob(attachment.contentBase64, attachment.mimeType || 'application/octet-stream');
                formData.append('attachments', blob, attachment.fileName || 'attachment');
            } else if (attachment instanceof Blob) {
                formData.append('attachments', attachment, attachment.name || attachment.fileName || 'attachment');
            }
        });

        return axios.post(
            `${API_URL}completeFSR/${completionData.appointmentNumber}/${completionData.sessionId}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
    };

    getPreviousWorkBySession = async (sessionId, customerId, locationId) => {
        return axios.get(`${API_URL}getPreviousFSRs/${sessionId}/${customerId}/${locationId}`);
    };

    getHistoricAppointments = async (token, filterObject) => {
        return axios.post(`${API_URL}historicAppointments/`, filterObject, {headers:{'Authorization':token}});
    };

    recreateFSR = async (token, appointmentId) => {
        return axios.post(`${API_URL}recreateFSR/${appointmentId}`, {}, {headers:{'Authorization':token}});
    };

    getCompletedFSRFile = async (token, filename) => {
        return axios.get(`${API_URL}getCompletedFSRFile/${filename}`, {
            headers:{'Authorization':token},
            responseType: 'blob'
        });
    };

    getAttachmentFile = async (token, filename) => {
        return axios.get(`${API_URL}getAppointmentAttachment/${filename}`, {
            headers:{'Authorization':token},
            responseType: 'blob'
        });
    };
    
    updateAdditionalComments = async (token, appointmentId, additionalComments) => {
        return axios.post(`${API_URL}updateAdditionalComments/${appointmentId}`, {additionalComments}, {headers:{'Authorization':token}});
    };

    getAllEquipmentRecords = async (token, filterObject) => {
        return axios.post(`${API_URL}getAllEquipmentRecords/`, filterObject, {headers:{'Authorization':token}});
    };

    getCustomerLocationEquipment = async (token, customerId, locationId) => {
        return axios.get(`${API_URL}getCustomerLocationEquipment/${customerId}/${locationId}`, {headers:{'Authorization':token}});
    };

    getCustomerLocationEquipmentBySession = async ( sessionId, customerId, locationId) => {
        return axios.get(`${API_URL}getCustomerLocationEquipmentBySession/${sessionId}/${customerId}/${locationId}`);
    };

    createEquipmentRecord = async (token, equipmentData) => {
        return axios.post(`${API_URL}createEquipmentRecord/`, equipmentData, {headers:{'Authorization':token}});
    };

    updateEquipmentRecord = async (token, equipmentData) => {
        return axios.post(`${API_URL}updateEquipmentRecord/`, equipmentData, {headers:{'Authorization':token}});
    };

    deleteEquipmentRecord = async (token, recordId) => {
        return axios.delete(`${API_URL}deleteEquipmentRecord/${recordId}`, {headers:{'Authorization':token}});
    };

    updateEquipmentRecords = async (sessionId, customerId, locationId, equipmentDataArray) => {
        return axios.post(`${API_URL}updateCustomerLocationEquipmentBySession/${sessionId}/${customerId}/${locationId}`, equipmentDataArray);
    };

};

FieldServiceAPI = new FieldServiceAPI();
export default FieldServiceAPI;